<div class="sales-overview-container" #dashboardContent>
  <!-- Header with Title and Filters -->
  <div class="header-section">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
      <h1 class="page-title">{{ 'i18n.dashboard.salesOverview' | translate }}</h1>
      <button *ngIf="!exporting" pButton type="button" icon="pi pi-download" (click)="exportDashboard()" [loading]="exporting" class="p-button-rounded p-button-info" title="Export Dashboard"></button>
    </div>
    <form [formGroup]="filterForm" class="filter-bar">
      <div class="filter-item">
        <label>{{ 'i18n.common.dateRange' | translate }}</label>
        <p-dropdown 
          [options]="dateRangeOptions" 
          formControlName="dateRangeType"
          optionLabel="label" 
          optionValue="value"
          (onChange)="onFilterChange()"
          [showClear]="false">
        </p-dropdown>
      </div>

      <div class="filter-item" *ngIf="filterForm.get('dateRangeType')?.value === 'Custom'">
        <label>{{ 'i18n.common.startDate' | translate }}</label>
        <p-calendar 
          formControlName="startDate"
          [showIcon]="true"
          (onSelect)="onFilterChange()">
        </p-calendar>
      </div>

      <div class="filter-item" *ngIf="filterForm.get('dateRangeType')?.value === 'Custom'">
        <label>{{ 'i18n.common.endDate' | translate }}</label>
        <p-calendar 
          formControlName="endDate"
          [showIcon]="true"
          (onSelect)="onFilterChange()">
        </p-calendar>
      </div>

      <div class="filter-item search-item">
        <label>{{ 'i18n.common.search' | translate }}</label>
        <input 
          pInputText 
          formControlName="productName"
          (keyup)="onFilterChange()"
          type="text"
          placeholder="{{ 'i18n.common.search' | translate }}">
      </div>
    </form>
  </div>

  <!-- KPI Cards -->
  <div class="kpi-cards-grid">
    <div class="kpi-card">
      <div class="kpi-label">{{ 'i18n.dashboard.activeProducts' | translate }}</div>
      <div class="kpi-value">{{ overview?.activeProducts || 0 }}</div>
    </div>

    <div class="kpi-card">
      <div class="kpi-label">{{ 'i18n.dashboard.stockSoldPercentage' | translate }}</div>
      <div class="kpi-value">{{ (overview?.stockSoldPercentage || 0) | number:'1.1-2' }}%</div>
    </div>

    <div class="kpi-card">
      <div class="kpi-label">{{ 'i18n.dashboard.deliveryPercentage' | translate }}</div>
      <div class="kpi-value">{{ (overview?.deliveryPercentage || 0) | number:'1.1-2' }}%</div>
    </div>

    <div class="kpi-card">
      <div class="kpi-label">{{ 'i18n.dashboard.soldRevenue' | translate }}</div>
      <div class="kpi-value">₹{{ (overview?.soldRevenue || 0) | number:'1.2-2' }}</div>
    </div>

    <div class="kpi-card">
      <div class="kpi-label">{{ 'i18n.dashboard.totalInventoryValue' | translate }}</div>
      <div class="kpi-value">₹{{ (overview?.totalInventoryValue || 0) | number:'1.2-2' }}</div>
    </div>
  </div>

  <!-- Revenue Trend and Alerts Section -->
  <div class="main-content-grid">
    <!-- Top Performing Products -->
    <div class="top-products-card" *ngIf="overview?.topProducts && overview.topProducts.length > 0">
      <h3 class="chart-header">{{ 'i18n.dashboard.topPerformingProducts' | translate }}</h3>
      <div class="top-products-list">
        <div class="top-product-item" *ngFor="let product of overview.topProducts; let i = index">
          <div class="product-rank">{{ i + 1 }}</div>
          <div class="product-info">
            <div class="product-name">{{ product.productName }}</div>
            <div class="product-stats">
              <span>{{ 'i18n.dashboard.sold' | translate }}: {{ product.soldQuantity }}</span>
              <span>{{ 'i18n.dashboard.revenue' | translate }}: ₹{{ (product.revenue || 0) | number:'1.2-2' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alerts Section -->
    <div class="alerts-section">
      <!-- Low Stock Alerts -->
      <div class="alert-card">
        <h3 class="alert-header">{{ 'i18n.dashboard.lowStockAlerts' | translate }}</h3>
        <div class="alert-list" *ngIf="overview?.lowStockAlerts && overview.lowStockAlerts.length > 0">
          <div class="alert-item" *ngFor="let alert of overview.lowStockAlerts">
            <div class="alert-content">
              <div class="alert-product">{{ alert.productName }} - {{ alert.currentStock }} {{ getProductUOM(alert.productId) }}</div>
            </div>
            <div class="alert-icon-warning">⚠️</div>
          </div>
        </div>
        <div class="alert-empty" *ngIf="!overview?.lowStockAlerts || overview.lowStockAlerts.length === 0">
          {{ 'i18n.dashboard.noRecordsFound' | translate }}
        </div>
      </div>

      <!-- Expiry Alerts -->
      <div class="alert-card">
        <h3 class="alert-header expiry-header">{{ 'i18n.dashboard.expiryAlerts' | translate }}</h3>
        <div class="alert-list" *ngIf="overview?.expiryAlerts && overview.expiryAlerts.length > 0">
          <div class="alert-item" *ngFor="let alert of overview.expiryAlerts">
            <div class="alert-content">
              <div class="alert-product">{{ alert.productName }} - {{ alert.daysToExpire }} Days</div>
            </div>
            <div class="alert-icon-warning">⚠️</div>
          </div>
        </div>
        <div class="alert-empty" *ngIf="!overview?.expiryAlerts || overview.expiryAlerts.length === 0">
          {{ 'i18n.dashboard.noRecordsFound' | translate }}
        </div>
      </div>
    </div>
  </div>

  <!-- Performance Breakdown -->
  <div class="performance-grid">
    <!-- Inventory Performance -->
    <div class="performance-card">
      <h3 class="chart-header">{{ 'i18n.dashboard.inventoryPerformance' | translate }}</h3>
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <p-chart type="doughnut" [data]="inventoryChartOptions.data" [options]="inventoryChartOptions" [style]="{'height': '150px', 'width': '180px'}"></p-chart>
        <div style="display: flex; flex-direction: column; gap: 20px; flex: 1; padding-left: 30px;">
          <div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="font-size: 13px; font-weight: 500; color: #FF9800;">{{ 'i18n.dashboard.inStock' | translate }}</span>
              <span style="font-size: 13px; font-weight: 600; color: #333;">{{ overview?.inventoryPerformance?.remainingStock || 0 }}</span>
            </div>
            <div style="height: 6px; background: #f0f0f0; border-radius: 3px; overflow: hidden;">
              <div [style.width.%]="((overview?.inventoryPerformance?.remainingStock || 0) / ((overview?.inventoryPerformance?.remainingStock || 0) + (overview?.inventoryPerformance?.totalSold || 0)) * 100) || 0" style="height: 100%; background: linear-gradient(90deg, #FF9800, #FFB74D); border-radius: 3px;"></div>
            </div>
          </div>
          <div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="font-size: 13px; font-weight: 500; color: #2196F3;">{{ 'i18n.dashboard.sold' | translate }}</span>
              <span style="font-size: 13px; font-weight: 600; color: #333;">{{ overview?.inventoryPerformance?.totalSold || 0 }}</span>
            </div>
            <div style="height: 6px; background: #f0f0f0; border-radius: 3px; overflow: hidden;">
              <div [style.width.%]="((overview?.inventoryPerformance?.totalSold || 0) / ((overview?.inventoryPerformance?.remainingStock || 0) + (overview?.inventoryPerformance?.totalSold || 0)) * 100) || 0" style="height: 100%; background: linear-gradient(90deg, #2196F3, #64B5F6); border-radius: 3px;"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delivery Performance -->
    <div class="performance-card">
      <h3 class="chart-header">{{ 'i18n.dashboard.deliveryPerformance' | translate }}</h3>
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <p-chart type="doughnut" [data]="deliveryChartOptions.data" [options]="deliveryChartOptions" [style]="{'height': '150px', 'width': '180px'}"></p-chart>
        <div style="display: flex; flex-direction: column; gap: 20px; flex: 1; padding-left: 30px;">
          <div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="font-size: 13px; font-weight: 500; color: #4CAF50;">{{ 'i18n.dashboard.delivered' | translate }}</span>
              <span style="font-size: 13px; font-weight: 600; color: #333;">{{ overview?.deliveryPerformance?.deliveredItems || 0 }}</span>
            </div>
            <div style="height: 6px; background: #f0f0f0; border-radius: 3px; overflow: hidden;">
              <div [style.width.%]="((overview?.deliveryPerformance?.deliveredItems || 0) / (overview?.deliveryPerformance?.totalOrderItems || 1) * 100) || 0" style="height: 100%; background: linear-gradient(90deg, #4CAF50, #81C784); border-radius: 3px;"></div>
            </div>
          </div>
          <div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="font-size: 13px; font-weight: 500; color: #2196F3;">{{ 'i18n.dashboard.pending' | translate }}</span>
              <span style="font-size: 13px; font-weight: 600; color: #333;">{{ (overview?.deliveryPerformance?.totalOrderItems || 0) - (overview?.deliveryPerformance?.deliveredItems || 0) }}</span>
            </div>
            <div style="height: 6px; background: #f0f0f0; border-radius: 3px; overflow: hidden;">
              <div [style.width.%]="(((overview?.deliveryPerformance?.totalOrderItems || 0) - (overview?.deliveryPerformance?.deliveredItems || 0)) / (overview?.deliveryPerformance?.totalOrderItems || 1) * 100) || 0" style="height: 100%; background: linear-gradient(90deg, #2196F3, #64B5F6); border-radius: 3px;"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Status Breakdown -->
    <div class="performance-card">
      <h3 class="chart-header">{{ 'i18n.dashboard.orderStatusBreakdown' | translate }}</h3>
      <div style="height: 200px; display: flex; align-items: center; justify-content: center;">
        <p-chart type="bar" [data]="orderStatusChartOptions.data" [options]="orderStatusChartOptions" [style]="{'height': '100%', 'width': '100%'}"></p-chart>
      </div>
    </div>
  </div>

  <!-- Revenue Trend Chart -->
  <div class="top-products-card">
    <h3 class="chart-header">{{ 'i18n.dashboard.revenueTrend' | translate }}</h3>
    <p-chart type="line" [data]="revenueChartOptions.data" [options]="revenueChartOptions" [style]="{'height': '350px'}"></p-chart>
  </div>
  <div class="data-table">
    <h3 class="chart-header">{{ 'i18n.dashboard.productSalesTable' | translate }}</h3>
    <p-table
      [value]="overview?.productsTable?.items || []"
      [paginator]="!disablePagination"
      [rows]="20"
      [rowsPerPageOptions]="[5, 10, 20]"
      [lazy]="false"
      responsiveLayout="scroll"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Products"
      [showCurrentPageReport]="true"
      styleClass="dt-grid"
    >
      <ng-template pTemplate="header">
        <tr>
          <th pSortableColumn="productName" class="col-name">
            {{ 'i18n.common.productName' | translate }}
            <p-sortIcon field="productName"></p-sortIcon>
          </th>
          <th pSortableColumn="publishedDate" class="col-date">
            {{ 'i18n.dashboard.publishedDate' | translate }}
            <p-sortIcon field="publishedDate"></p-sortIcon>
          </th>
          <th pSortableColumn="originalQuantity" class="dt-num">
            {{ 'i18n.dashboard.originalQuantity' | translate }}
            <p-sortIcon field="originalQuantity"></p-sortIcon>
          </th>
          <th pSortableColumn="currentStock" class="dt-num">
            {{ 'i18n.dashboard.currentStock' | translate }}
            <p-sortIcon field="currentStock"></p-sortIcon>
          </th>
          <th>{{ 'i18n.dashboard.uom' | translate }}</th>
          <th pSortableColumn="soldQuantity" class="dt-num">
            {{ 'i18n.dashboard.sold' | translate }}
            <p-sortIcon field="soldQuantity"></p-sortIcon>
          </th>
          <th pSortableColumn="numberOfOrders" class="dt-num">
            {{ 'i18n.dashboard.numberOfOrders' | translate }}
            <p-sortIcon field="numberOfOrders"></p-sortIcon>
          </th>
          <th pSortableColumn="salePercentage" class="dt-num">
            {{ 'i18n.dashboard.salePercentage' | translate }}
            <p-sortIcon field="salePercentage"></p-sortIcon>
          </th>
          <th pSortableColumn="daysToExpire" class="dt-num">
            {{ 'i18n.dashboard.daysToExpire' | translate }}
            <p-sortIcon field="daysToExpire"></p-sortIcon>
          </th>
          <th pSortableColumn="amount" class="dt-num col-amount">
            {{ 'i18n.dashboard.amount' | translate }} (₹)
            <p-sortIcon field="amount"></p-sortIcon>
          </th>
          <th pSortableColumn="deliveryPercentage" class="dt-num">
            {{ 'i18n.dashboard.deliveryPercentage' | translate }}
            <p-sortIcon field="deliveryPercentage"></p-sortIcon>
          </th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-product>
        <tr>
          <td class="col-name">{{ product.productName }}</td>
          <td class="col-date">{{ product.publishedDate | date: 'dd-MM-yyyy' }}</td>
          <td class="dt-num">{{ product.originalQuantity }}</td>
          <td class="dt-num">{{ product.currentStock }}</td>
          <td>{{ product.unitOfMeasure }}</td>
          <td class="dt-num">{{ product.soldQuantity }}</td>
          <td class="dt-num">{{ product.numberOfOrders }}</td>
          <td class="dt-num">
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="product.salePercentage" [ngClass]="getSalePercentageClass(product.salePercentage)"></div>
              <span class="progress-text">{{ (product.salePercentage || 0) | number:'1.1-2' }}%</span>
            </div>
          </td>
          <td class="dt-num">
            <p-tag 
              *ngIf="product.daysToExpire !== 2147483647 && product.daysToExpire >= 0"
              [value]="product.daysToExpire + ' days'" 
              [severity]="getDaysToExpireClass(product.daysToExpire)">
            </p-tag>
            <p-tag 
              *ngIf="product.daysToExpire < 0"
              [value]="'i18n.dashboard.expired' | translate" 
              severity="danger">
            </p-tag>
            <span *ngIf="product.daysToExpire === 2147483647">-</span>
          </td>
          <td class="dt-num col-amount">{{ (product.amount || 0) | number: '1.2-2' }}</td>
          <td class="dt-num">
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="product.deliveryPercentage" [ngClass]="getDeliveryPercentageClass(product.deliveryPercentage)"></div>
              <span class="progress-text">{{ (product.deliveryPercentage || 0) | number:'1.1-2' }}%</span>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="11">
            {{ 'i18n.dashboard.noProductsFound' | translate }}
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>
